import styled from 'styled-components';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PatternFormat } from 'react-number-format';

import { config } from '_config';
import { theme } from '_constants/theme';
import { FIELDS_KEYS } from '_constants/forms';
import { ICONS_ALIASES } from '_constants/icons';
import { getHostname } from '_helpers/common';

import { sendMessage } from '_redux/contactUs/slice';
import {
  selectResult,
  selectError,
  selectLoading,
} from '_redux/contactUs/selector';

import * as Layout from '_components/atomic/layouts';
import MainBannerSection from '_components/composed/sections/MainBannerSection';
import ContactUsForm from '_components/composed/forms/ContactUsForm';
import Icon from '_components/atomic/Icon';

const StyledContactUsPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledLink = styled.a`
  display: inline-block;
  margin-left: ${theme.spaces.regular}px;
  margin-right: ${theme.spaces.regular}px;
  padding-top: ${theme.spaces.smallX}px;
  padding-bottom: ${theme.spaces.smallX}px;
  white-space: nowrap;
  color: ${theme.colors.indigo[600]};
`;

const ContactUsPage = () => {
  const dispatch = useDispatch();
  const result = useSelector(selectResult);
  const error = useSelector(selectError);
  const loading = useSelector(selectLoading);

  const handleSubmit = useCallback(
    (values) => {
      if (values) {
        dispatch(
          sendMessage({
            category: 'INC',
            domain: getHostname(),
            name: values[FIELDS_KEYS.fullName],
            email: values[FIELDS_KEYS.email],
            subject: values[FIELDS_KEYS.subject],
            message: values[FIELDS_KEYS.message],
          })
        );
      }
    },
    [dispatch]
  );

  return (
    <StyledContactUsPage>
      <MainBannerSection
        title="Contact us"
        topText={
          <>
            If you have any questions or need assistance with our services,
            please do not hesitate to reach out to us.
            <br />
            Our team is available to answer your questions and provide support
            Monday through Friday from 9am to 5pm.
            <br />
            You can contact us via phone, email, or by filling out the form on
            this page.
            <br />
            <br />
            <StyledLink href={'tel:' + config.phone}>
              <Icon
                alias={ICONS_ALIASES.phone}
                color={theme.colors.indigo[600]}
              />
              <PatternFormat
                value={config.phone?.substring(2)}
                displayType="text"
                format="(###) ###-####"
              />
            </StyledLink>
            <StyledLink href={`mailto:${config.email}`}>
              {config.email}
            </StyledLink>
          </>
        }
        desktopImage={`${config.basePath}/images/contact-us-banner-desktop.png`}
        mobileImage={`${config.basePath}/images/contact-us-banner-desktop.png`}
        bottomText={
          <>
            If you prefer to contact us by filling out the form on this page,
            please provide your name, email address, and a brief message
            describing how we can help you.
            <br />
            We will do our best to respond to your inquiry as soon as possible.
          </>
        }
      />

      <Layout.Divider size={theme.spaces.large6X} desktop />
      <Layout.Divider size={theme.spaces.large} mobile />

      <ContactUsForm
        error={error}
        success={result}
        submitDisabled={loading}
        onSubmit={handleSubmit}
      />

      <Layout.Divider size={theme.spaces.large24X} desktop />
      <Layout.Divider size={theme.spaces.large} mobile />
    </StyledContactUsPage>
  );
};

export default ContactUsPage;
